import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useHasAccess } from '../../../hooks/auth'
import { useOsConfig, useOsDetail, useOsTecnicos } from '../../../hooks/os'
import {
  approveOs,
  createOsAnexoUrl,
  deleteOsAnexo,
  deleteOsCustoLogistica,
  getOsSetores,
  importOs,
  importOsParcial,
  rejectOs,
  reopenOs,
  reverterImportOs,
  saveOs,
  saveOsAnexo,
  saveOsCustoLogistica,
  submitOs,
} from '../../../Service/os'
import {
  OS_PERM,
  OsDetail as OsDetailType,
  OsEquipamento,
  OsEquipamentoTipo,
  OsLeitorSetor,
  OsMaquina,
  OsStatus,
  OsTipo,
  SaveOsPayload,
} from '../../../types/os'
import {
  BD,
  Button,
  Card,
  DetailLayout,
  Field,
  Grid2,
  Grid3,
  Input,
  PageTitle,
  Pill,
  Select,
  StatusBadge,
} from '../styles'
import { MoneyInput } from '../MoneyInput'
import {
  calcAlimLocal,
  calcAlimLocalTotal,
  calcAlimViagem,
  calcCacheTurno,
  calcExtras,
  calcLeitoresSugeridos,
  cfgFrom,
  diffMin,
} from '../osCalc'
import { fmtDate, fmtDateTime, fmtMoney, FECHAMENTO_UI, STATUS_UI } from '../statusUi'
import { PAINEL_URL } from '../../../config'
import { LOCAL_EXECUTION } from '../../../utils/featureFlag'

const EQUIP_TIPOS: OsEquipamentoTipo[] = ['LEITOR', 'SMART', 'NETBOOK', 'NOTEBOOK', 'TEF', 'EPSON', 'ZEBRA', 'CELULAR', 'CARREGADOR']
const ORIGENS = ['BD', 'OLC', 'ROSEMBERG']
const EDITABLE_STATUS: OsStatus[] = ['EM_ABERTO', 'EM_PREENCHIMENTO', 'REJEITADA']

interface FormTurno { data: string; chegada: string; saida: string }
interface FormTec { tecnicoId: number; alimDiasViagem: number; turnos: FormTurno[] }
interface FormState {
  temAtendimento: number | null
  tipo: OsTipo
  obsAtendimento: string
  custos: {
    staff: number; kmRodado: number; combustivel: number; pedagio: number
    aluguelCarro: number; uber: number; estacionamento: number; hospedagem: number; outros: string
  }
  equipe: FormTec[]
  leitorSetores: OsLeitorSetor[]
  maquinas: OsMaquina[]
  equipamentos: OsEquipamento[]
}

const num = (v: any): number => Number(v ?? 0) || 0

// "2026-06-18T16:00:00.000Z" | "2026-06-18 16:00:00" -> "2026-06-18T16:00" (datetime-local)
const toDatetimeLocal = (v?: string): string => (v ? v.replace(' ', 'T').slice(0, 16) : '')
// "2026-06-18..." -> "2026-06-18" (date input)
const toDateInput = (v?: string): string => (v ? v.slice(0, 10) : '')

const SectionCard: FC<{ title: string; right?: React.ReactNode; children: React.ReactNode }> = ({ title, right, children }) => (
  <Card style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <h3 style={{ fontWeight: 700, fontSize: 15 }}>{title}</h3>
      {right && <div style={{ marginLeft: 'auto' }}>{right}</div>}
    </div>
    {children}
  </Card>
)

export const OsDetail: FC = () => {
  const { id } = useParams<{ id: string }>()
  const osId = Number(id)
  const navigate = useNavigate()
  const { detail, mutate, isUnauthorized } = useOsDetail(osId)
  const { config } = useOsConfig()
  const { tecnicos } = useOsTecnicos()

  const canEdit = useHasAccess(OS_PERM.LANCAR)
  const canApprove = useHasAccess(OS_PERM.APROVAR)
  const canImport = useHasAccess(OS_PERM.IMPORTAR)
  const canRevert = useHasAccess(OS_PERM.REVERTER)
  const canPartialImport = useHasAccess(OS_PERM.IMPORTAR_PARCIAL)
  const cfg = cfgFrom(config)

  const [form, setForm] = useState<FormState | null>(null)
  const [saving, setSaving] = useState(false)
  const loadedRef = useRef<number | null>(null)
  const [anexoFile, setAnexoFile] = useState<File | null>(null)
  const [uploadingAnexo, setUploadingAnexo] = useState(false)
  const [deletingAnexoId, setDeletingAnexoId] = useState<number | null>(null)
  const [logForm, setLogForm] = useState<{ data: string; descricao: string; valor: number }>({ data: '', descricao: '', valor: 0 })
  const [savingLog, setSavingLog] = useState(false)
  const [viewAnexo, setViewAnexo] = useState<{ url: string; tipo: string | null; nome: string } | null>(null)

  const buildBase = (d: OsDetailType): FormState => ({
    temAtendimento: d.os.temAtendimento,
    tipo: d.os.tipo ?? 'LOCAL',
    obsAtendimento: d.os.obsAtendimento ?? '',
    custos: {
      staff: num(d.custos?.staff),
      kmRodado: num(d.custos?.kmRodado),
      combustivel: num(d.custos?.combustivel),
      pedagio: num(d.custos?.pedagio),
      aluguelCarro: num(d.custos?.aluguelCarro),
      uber: num(d.custos?.uber),
      estacionamento: num(d.custos?.estacionamento),
      hospedagem: num(d.custos?.hospedagem),
      outros: d.custos?.outros ?? '',
    },
    equipe: d.equipe.map((e) => ({
      tecnicoId: e.tecnicoId,
      alimDiasViagem: e.alimDiasViagem ?? 0,
      turnos: (e.turnos ?? []).map((t) => ({
        data: toDateInput(t.data),
        chegada: toDatetimeLocal(t.chegada),
        saida: toDatetimeLocal(t.saida),
      })),
    })),
    leitorSetores: d.leitorSetores.map((s) => ({ ...s })),
    maquinas: d.maquinas.map((m) => ({ ...m })),
    equipamentos: d.equipamentos.map((e) => ({ tipo: e.tipo, qtd: e.qtd, valorUnit: num(e.valorUnit) })),
  })

  // Quebra por setor: OS editável re-sincroniza com os setores atuais do evento (P1).
  // Setor criado no evento DEPOIS do primeiro save não aparecia (o seed antigo só
  // rodava com a lista vazia). Preserva o preenchido (utilizados/origem) e atualiza
  // emitidos/sugeridos com o dado vivo; setor salvo que sumiu do evento é mantido.
  const mergeLeitorSetores = (
    saved: OsLeitorSetor[],
    doEvento: OsLeitorSetor[],
  ): OsLeitorSetor[] => {
    const porSetorId = new Map<number, OsLeitorSetor>()
    saved.forEach((s) => {
      if (s.setorId) porSetorId.set(s.setorId, s)
    })
    const doEventoIds = new Set<number>()
    const merged = doEvento.map((ev) => {
      if (ev.setorId) doEventoIds.add(ev.setorId)
      const s = ev.setorId ? porSetorId.get(ev.setorId) : undefined
      return s
        ? { ...s, setor: ev.setor, emitidos: ev.emitidos, leitoresSugeridos: ev.leitoresSugeridos }
        : { ...ev }
    })
    const orfaos = saved.filter((s) => !s.setorId || !doEventoIds.has(s.setorId))
    return merged.concat(orfaos)
  }

  // Inicializa o form UMA vez por OS. Revalidações do SWR não re-inicializam
  // (não apagam o que o usuário está preenchendo). Re-sincroniza só após salvar.
  useEffect(() => {
    if (!detail) return
    if (loadedRef.current === detail.os.id) return
    loadedRef.current = detail.os.id
    const base = buildBase(detail)
    if (detail.os.eventoId && EDITABLE_STATUS.includes(detail.os.status)) {
      getOsSetores(detail.os.eventoId)
        .then((res) =>
          setForm({
            ...base,
            leitorSetores: mergeLeitorSetores(base.leitorSetores, res.data?.data ?? []),
          }),
        )
        .catch(() => setForm(base))
    } else {
      setForm(base)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail])

  const resumo = useMemo(() => {
    const logistica = (detail?.custoLogistica ?? []).reduce((s, l) => s + Number(l.valor), 0)
    if (!form) return { cache: 0, alim: 0, desloc: 0, hosp: 0, staff: 0, op: 0, equip: 0, logistica, total: logistica }
    const cache = form.equipe.reduce(
      (s, t) => s + t.turnos.reduce((ss, tr) => ss + calcCacheTurno(diffMin(tr.chegada, tr.saida), cfg), 0),
      0,
    )
    const alim = form.equipe.reduce((s, t) => {
      if (form.tipo === 'VIAGEM') return s + calcAlimViagem(t.alimDiasViagem, cfg)
      // POR TURNO: cada turno com extras >= limiar soma uma alimentação
      return s + calcAlimLocalTotal(t.turnos.map((tr) => diffMin(tr.chegada, tr.saida)), cfg)
    }, 0)
    const c = form.custos
    const desloc = c.kmRodado * cfg.kmValor + c.combustivel + c.pedagio + c.aluguelCarro + c.uber + c.estacionamento
    const equip = form.equipamentos.reduce((s, e) => s + num(e.qtd) * num(e.valorUnit), 0)
    const conta = form.temAtendimento !== 0
    const op = conta ? cache + c.staff + alim + desloc + c.hospedagem : 0
    return { cache, alim, desloc, hosp: c.hospedagem, staff: c.staff, op, equip, logistica, total: op + equip + logistica }
  }, [form, cfg, detail?.custoLogistica])

  // Backend negou por sessão vencida (rede de segurança do OP-3462)
  if (isUnauthorized && !LOCAL_EXECUTION) {
    return <Navigate to="/auth" replace />
  }

  if (!detail || !form) return <div style={{ padding: 40, color: BD.grayText }}>Carregando…</div>

  const status = detail.os.status
  const editable = EDITABLE_STATUS.includes(status) && canEdit
  const ui = STATUS_UI[status]
  const showAtend = form.temAtendimento !== 0

  const patch = (p: Partial<FormState>) => setForm((f) => (f ? { ...f, ...p } : f))
  const patchCustos = (k: keyof FormState['custos'], v: any) =>
    setForm((f) => (f ? { ...f, custos: { ...f.custos, [k]: k === 'outros' ? v : num(v) } } : f))

  const setAtendimento = (val: number) => {
    patch({ temAtendimento: val })
  }

  // Equipe
  const addTecnico = () => patch({ temAtendimento: 1, equipe: [...form.equipe, { tecnicoId: 0, alimDiasViagem: 0, turnos: [] }] })
  const rmTecnico = (i: number) => patch({ equipe: form.equipe.filter((_, idx) => idx !== i) })
  const updTec = (i: number, p: Partial<FormTec>) =>
    patch({ equipe: form.equipe.map((t, idx) => (idx === i ? { ...t, ...p } : t)) })
  const addTurno = (i: number) =>
    updTec(i, { turnos: [...form.equipe[i].turnos, { data: '', chegada: '', saida: '' }] })
  const updTurno = (i: number, j: number, p: Partial<FormTurno>) =>
    updTec(i, { turnos: form.equipe[i].turnos.map((tr, idx) => (idx === j ? { ...tr, ...p } : tr)) })
  const rmTurno = (i: number, j: number) =>
    updTec(i, { turnos: form.equipe[i].turnos.filter((_, idx) => idx !== j) })

  // Setores / máquinas / equipamentos
  const updSetor = (i: number, p: Partial<OsLeitorSetor>) =>
    patch({ leitorSetores: form.leitorSetores.map((s, idx) => (idx === i ? { ...s, ...p } : s)) })
  const addMaquina = () => patch({ maquinas: [...form.maquinas, { tipo: '', serial: '' }] })
  const updMaquina = (i: number, p: Partial<OsMaquina>) =>
    patch({ maquinas: form.maquinas.map((m, idx) => (idx === i ? { ...m, ...p } : m)) })
  const rmMaquina = (i: number) => patch({ maquinas: form.maquinas.filter((_, idx) => idx !== i) })
  // Valor por unidade default do equipamento, vindo da config (pré-preenche; pode editar).
  const equipValorCfg = (tipo: OsEquipamentoTipo): number => {
    const k = `equipValor${tipo.charAt(0)}${tipo.slice(1).toLowerCase()}` as keyof typeof config
    return config ? num((config as any)[k]) : 0
  }
  const addEquip = () =>
    patch({ equipamentos: [...form.equipamentos, { tipo: 'LEITOR', qtd: 1, valorUnit: equipValorCfg('LEITOR') }] })
  const updEquip = (i: number, p: Partial<OsEquipamento>) =>
    patch({
      equipamentos: form.equipamentos.map((e, idx) => {
        if (idx !== i) return e
        // Ao trocar o tipo, repõe o valor unitário sugerido pela config.
        const next = { ...e, ...p }
        if (p.tipo && p.tipo !== e.tipo) next.valorUnit = equipValorCfg(p.tipo)
        return next
      }),
    })
  const rmEquip = (i: number) => patch({ equipamentos: form.equipamentos.filter((_, idx) => idx !== i) })

  const buildPayload = (): SaveOsPayload => ({
    id: osId,
    temAtendimento: form.temAtendimento,
    tipo: form.tipo,
    obsAtendimento: form.obsAtendimento,
    custos: form.custos,
    equipe: form.equipe
      .filter((t) => t.tecnicoId)
      .map((t) => ({ tecnicoId: t.tecnicoId, alimDiasViagem: t.alimDiasViagem, turnos: t.turnos.filter((tr) => tr.chegada && tr.saida) })),
    leitorSetores: form.leitorSetores,
    maquinas: form.maquinas.filter((m) => m.tipo),
    equipamentos: form.equipamentos,
  })

  // Re-sincroniza o form a partir do dado fresco (após salvar/transição)
  const resync = async () => {
    const res = await mutate()
    const fresh = res?.data?.data as OsDetailType | undefined
    if (fresh) setForm(buildBase(fresh))
  }
  const doSave = async (): Promise<boolean> => {
    // Leitor utilizado exige origem (o backend também valida — aqui é o aviso amigável)
    const semOrigem = form.leitorSetores.filter((s) => (s.leitoresUtilizados ?? 0) > 0 && !s.origem)
    if (semOrigem.length) {
      toast.error(`Informe a origem dos leitores no(s) setor(es): ${semOrigem.map((s) => s.setor).join(', ')}`)
      return false
    }
    setSaving(true)
    try {
      await saveOs(buildPayload())
      toast.success('OS salva')
      await resync()
      return true
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erro ao salvar')
      return false
    } finally {
      setSaving(false)
    }
  }
  const doTransition = async (fn: () => Promise<any>, msg: string) => {
    setSaving(true)
    try {
      await fn()
      toast.success(msg)
      await resync()
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erro na operação')
    } finally {
      setSaving(false)
    }
  }
  // Anexo: opera no servidor e atualiza só a lista (sem re-sincronizar o form,
  // p/ não perder o que está sendo preenchido).
  const removeAnexo = async (id: number) => {
    setDeletingAnexoId(id)
    try {
      await deleteOsAnexo(id)
      toast.success('Anexo removido')
      await mutate()
    } catch {
      toast.error('Erro ao remover anexo')
    } finally {
      setDeletingAnexoId(null)
    }
  }
  const doSubmit = async () => {
    if (!(await doSave())) return
    await doTransition(() => submitOs(osId), 'Enviada para aprovação')
  }
  const doReject = () => {
    const motivo = window.prompt('Motivo da rejeição:')
    if (motivo && motivo.trim()) doTransition(() => rejectOs(osId, motivo.trim()), 'OS rejeitada')
  }
  const IMPORT_ERRO: Record<string, string> = {
    FECHAMENTO_NAO_ENCONTRADO: 'O evento ainda não possui fechamento no P2.',
    FECHAMENTO_NAO_PRE_SUBMETIDO: 'O fechamento do evento não está em "pré-submetido" — operação bloqueada.',
    OS_NAO_APROVADA: 'A OS precisa estar aprovada para importar.',
    OS_JA_IMPORTADA: 'Esta OS já foi importada. Reverta antes de importar de novo.',
    OS_SEM_ITENS_A_LANCAR: 'Não há valores a lançar nesta OS.',
    NADA_A_IMPORTAR: 'Nada a importar — o fechamento já reflete os custos da OS.',
    OS_NAO_IMPORTADA: 'Esta OS não está importada.',
  }
  const doImportAction = async (fn: () => Promise<any>, msg: string) => {
    setSaving(true)
    try {
      await fn()
      toast.success(msg)
      await resync()
    } catch (e: any) {
      const code = e?.response?.data?.message
      toast.error(IMPORT_ERRO[code] ?? code ?? 'Erro na operação')
    } finally {
      setSaving(false)
    }
  }
  const doImport = () => {
    if (!window.confirm('Lançar os custos desta OS no fechamento do P2?')) return
    doImportAction(() => importOs(osId), 'OS importada para o P2')
  }
  const doReverter = () => {
    if (!window.confirm('Reverter a importação? Os itens lançados serão removidos do P2.')) return
    doImportAction(() => reverterImportOs(osId), 'Importação revertida')
  }
  const doImportarParcial = async () => {
    if (!window.confirm('Importar parcialmente os custos já lançados (cobrança/retenção/logística) no fechamento? O rascunho será salvo antes.')) return
    // A importação lê o banco: salva o rascunho antes para o P2 receber o que está na tela.
    if (editable && !(await doSave())) return
    doImportAction(() => importOsParcial(osId), 'Importação parcial concluída')
  }
  // Logística de Equipamentos (gerenciada à parte, como anexos)
  const addLogistica = async () => {
    if (!logForm.descricao.trim() || !logForm.data) {
      toast.warn('Preencha data e descrição')
      return
    }
    setSavingLog(true)
    try {
      await saveOsCustoLogistica({ osId, data: logForm.data, descricao: logForm.descricao.trim(), valor: logForm.valor })
      setLogForm({ data: '', descricao: '', valor: 0 })
      await mutate()
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erro ao adicionar custo')
    } finally {
      setSavingLog(false)
    }
  }
  const removeLogistica = async (id: number) => {
    if (!window.confirm('Remover este custo de logística?')) return
    try {
      await deleteOsCustoLogistica(osId, id)
      await mutate()
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erro ao remover custo')
    }
  }

  // Upload explícito (não depende de salvar a OS) — só grava o anexo se o PUT no S3 der certo
  const enviarAnexo = async () => {
    if (!anexoFile) return
    setUploadingAnexo(true)
    try {
      const { data } = await createOsAnexoUrl(osId, anexoFile.type)
      const put = await fetch(data.data.url, {
        method: 'PUT',
        headers: { 'Content-Type': anexoFile.type },
        body: anexoFile,
      })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      await saveOsAnexo({
        osId,
        key: data.data.key,
        nomeDespesa: anexoFile.name,
        tipo: anexoFile.type,
        tamanho: anexoFile.size,
      })
      toast.success('Anexo enviado')
      setAnexoFile(null)
      await mutate()
    } catch {
      toast.error('Falha no upload do anexo')
    } finally {
      setUploadingAnexo(false)
    }
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
        <span style={{ cursor: 'pointer' }} onClick={() => navigate('/interno/os')}>Ordens de Serviço</span>
        <span style={{ margin: '0 8px' }}>›</span>
        <span style={{ color: BD.red, fontWeight: 600 }}>OS {detail.os.numero}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PageTitle style={{ margin: 0 }}>OS {detail.os.numero}</PageTitle>
            <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge>
          </div>
          <div style={{ color: BD.grayText, fontSize: 13, marginTop: 4 }}>{detail.evento?.evento ?? '—'}</div>
          {detail.evento?.eventoCancelado ? (
            <Pill $bg="#FEE2E2" $color="#991B1B">⚠️ Evento cancelado</Pill>
          ) : null}
          <div
            style={{ fontSize: 12, color: BD.red, fontWeight: 600, marginTop: 6, cursor: 'pointer' }}
            onClick={() => navigate(`/events/${detail.os.eventoId}`)}
          >
            ↗ Ver tela do evento
          </div>
        </div>
        <div style={{ fontSize: 12, color: BD.grayText, textAlign: 'right' }}>
          Criada em {fmtDate(detail.os.dataHoraCriacao)}
        </div>
      </div>

      <DetailLayout>
        {/* ---- Coluna principal ---- */}
        <div>
          {/* 1. Evento (read-only) */}
          <SectionCard title="1. Informações do Evento">
            <Grid2>
              <Field><label>Praça</label><Input readOnly value={detail.evento?.praca ?? ''} /></Field>
              <Field><label>Produtor</label><Input readOnly value={detail.evento?.produtor ?? ''} /></Field>
              <Field><label>Evento</label><Input readOnly value={detail.evento?.evento ?? ''} /></Field>
              <Field><label>Data Término</label><Input readOnly value={fmtDate(detail.evento?.dataTermino)} /></Field>
              <Field><label>Local</label><Input readOnly value={detail.evento?.local ?? ''} /></Field>
              <Field><label>Ingressos Emitidos</label><Input readOnly value={String(detail.totalEmitidos)} /></Field>
              <Field>
                <label>Fechamento (P2)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38 }}>
                  {detail.evento?.fechamentoStatus ? (
                    <StatusBadge
                      $bg={FECHAMENTO_UI[detail.evento.fechamentoStatus]?.bg ?? '#F3F4F6'}
                      $color={FECHAMENTO_UI[detail.evento.fechamentoStatus]?.color ?? '#4B5563'}
                    >
                      {FECHAMENTO_UI[detail.evento.fechamentoStatus]?.label ?? detail.evento.fechamentoStatus}
                    </StatusBadge>
                  ) : (
                    <span style={{ color: '#9CA3AF', fontSize: 13 }}>Sem fechamento</span>
                  )}
                  <a
                    href={`${(PAINEL_URL || '').replace(/\/$/, '')}/painel/fechamento/cadastro/${detail.os.eventoId}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: BD.red, fontSize: 13 }}
                  >
                    abrir no P2 ↗
                  </a>
                </div>
              </Field>
            </Grid2>
          </SectionCard>

          {/* Toggle atendimento */}
          <SectionCard title="Atendimento Presencial">
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                $variant={form.temAtendimento === 1 ? 'primary' : 'secondary'}
                disabled={!editable}
                onClick={() => setAtendimento(1)}
              >✓ Sim</Button>
              <Button
                $variant={form.temAtendimento === 0 ? 'primary' : 'secondary'}
                disabled={!editable}
                onClick={() => setAtendimento(0)}
              >✕ Não</Button>
            </div>
          </SectionCard>

          {showAtend && (
            <>
              {/* 2. Cobrança */}
              <SectionCard title="2. Cobrança — Técnicos e Custos">
                <Field style={{ maxWidth: 240, marginBottom: 16 }}>
                  <label>Tipo de Evento</label>
                  <Select value={form.tipo} disabled={!editable} onChange={(e) => patch({ tipo: e.target.value as OsTipo })}>
                    <option value="LOCAL">🏙️ Local</option>
                    <option value="VIAGEM">✈️ Viagem</option>
                  </Select>
                </Field>

                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Técnicos</div>
                {form.equipe.map((t, i) => {
                  const cacheTec = t.turnos.reduce((s, tr) => s + calcCacheTurno(diffMin(tr.chegada, tr.saida), cfg), 0)
                  return (
                    <div key={i} style={{ background: '#FAFAFA', border: `1px solid ${BD.grayBorder}`, borderRadius: 8, padding: 14, marginBottom: 12 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <Field style={{ flex: 1 }}>
                          <label>Técnico</label>
                          <Select value={t.tecnicoId || ''} disabled={!editable} onChange={(e) => updTec(i, { tecnicoId: Number(e.target.value) })}>
                            <option value="">— Selecione —</option>
                            {tecnicos.map((tc) => <option key={tc.id} value={tc.id}>{tc.nome}</option>)}
                          </Select>
                        </Field>
                        {editable && (
                          <Button $variant="secondary" onClick={() => rmTecnico(i)} style={{ padding: '8px 12px' }}>Remover</Button>
                        )}
                      </div>

                      {/* Turnos */}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>Turnos (um por dia trabalhado)</div>
                        {t.turnos.map((tr, j) => {
                          const min = diffMin(tr.chegada, tr.saida)
                          return (
                            <div key={j} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 6 }}>
                              <Field style={{ width: 140 }}><label>Data</label><Input type="date" value={tr.data} disabled={!editable} onChange={(e) => updTurno(i, j, { data: e.target.value })} /></Field>
                              <Field style={{ flex: 1 }}><label>Chegada</label><Input type="datetime-local" value={tr.chegada} disabled={!editable} onChange={(e) => updTurno(i, j, { chegada: e.target.value })} /></Field>
                              <Field style={{ flex: 1 }}><label>Saída</label><Input type="datetime-local" value={tr.saida} disabled={!editable} onChange={(e) => updTurno(i, j, { saida: e.target.value })} /></Field>
                              <div style={{ width: 110, fontSize: 12, paddingBottom: 8, color: BD.grayText }}>
                                {calcExtras(min, cfg)}h ex · {fmtMoney(calcCacheTurno(min, cfg))}
                                {form.tipo !== 'VIAGEM' && calcAlimLocal(min, cfg) > 0 && (
                                  <span title={`Extras ≥ ${cfg.alimLocalMinExtras}h neste turno: soma ${fmtMoney(cfg.alimLocal)} de alimentação`}> · 🍽</span>
                                )}
                              </div>
                              {editable && <Button $variant="secondary" onClick={() => rmTurno(i, j)} style={{ padding: '6px 10px' }}>✕</Button>}
                            </div>
                          )
                        })}
                        {editable && <Button $variant="secondary" onClick={() => addTurno(i)} style={{ padding: '6px 12px', fontSize: 12 }}>+ Adicionar turno</Button>}
                      </div>

                      {form.tipo === 'VIAGEM' && (
                        <Field style={{ maxWidth: 200, marginTop: 10 }}>
                          <label>✈️ Diárias de alimentação</label>
                          <Input type="number" value={t.alimDiasViagem || ''} disabled={!editable} onChange={(e) => updTec(i, { alimDiasViagem: num(e.target.value) })} />
                          <div style={{ fontSize: 12, color: '#1D4ED8', marginTop: 4 }}>{t.alimDiasViagem} × {fmtMoney(cfg.alimViagemDia)} = {fmtMoney(calcAlimViagem(t.alimDiasViagem, cfg))}</div>
                        </Field>
                      )}

                      {form.tipo !== 'VIAGEM' && (() => {
                        const turnosQualif = t.turnos.filter((tr) => calcAlimLocal(diffMin(tr.chegada, tr.saida), cfg) > 0).length
                        const alimTec = calcAlimLocalTotal(t.turnos.map((tr) => diffMin(tr.chegada, tr.saida)), cfg)
                        return turnosQualif > 0 ? (
                          <div style={{ textAlign: 'right', marginTop: 8, fontSize: 12, color: '#1D4ED8' }}>
                            🍽 Alimentação: {turnosQualif} turno(s) c/ extras ≥ {cfg.alimLocalMinExtras}h × {fmtMoney(cfg.alimLocal)} = {fmtMoney(alimTec)}
                          </div>
                        ) : null
                      })()}
                      <div style={{ textAlign: 'right', marginTop: 8, fontWeight: 600, color: BD.red }}>Cachê: {fmtMoney(cacheTec)}</div>
                    </div>
                  )
                })}
                {editable && <Button $variant="secondary" onClick={addTecnico} style={{ fontSize: 12, padding: '6px 12px' }}>+ Adicionar técnico</Button>}

                <Field style={{ maxWidth: 240, marginTop: 16 }}>
                  <label>Staff (R$)</label>
                  <MoneyInput value={form.custos.staff} disabled={!editable} onChange={(v) => patchCustos('staff', v)} />
                </Field>

                <div style={{ fontWeight: 600, fontSize: 14, marginTop: 16, marginBottom: 8 }}>Deslocamento</div>
                <Grid3>
                  <Field><label>Km Rodado</label><Input type="number" value={form.custos.kmRodado || ''} disabled={!editable} onChange={(e) => patchCustos('kmRodado', e.target.value)} /></Field>
                  <Field><label>Combustível (R$)</label><MoneyInput value={form.custos.combustivel} disabled={!editable} onChange={(v) => patchCustos('combustivel', v)} /></Field>
                  <Field><label>Pedágio (R$)</label><MoneyInput value={form.custos.pedagio} disabled={!editable} onChange={(v) => patchCustos('pedagio', v)} /></Field>
                  <Field><label>Aluguel Carro (R$)</label><MoneyInput value={form.custos.aluguelCarro} disabled={!editable} onChange={(v) => patchCustos('aluguelCarro', v)} /></Field>
                  <Field><label>Uber (R$)</label><MoneyInput value={form.custos.uber} disabled={!editable} onChange={(v) => patchCustos('uber', v)} /></Field>
                  <Field><label>Estacionamento (R$)</label><MoneyInput value={form.custos.estacionamento} disabled={!editable} onChange={(v) => patchCustos('estacionamento', v)} /></Field>
                </Grid3>

                <Grid2 style={{ marginTop: 16 }}>
                  <Field><label>Hospedagem (R$)</label><MoneyInput value={form.custos.hospedagem} disabled={!editable} onChange={(v) => patchCustos('hospedagem', v)} /></Field>
                  <Field><label>Outros (texto, sem valor)</label><Input maxLength={1000} value={form.custos.outros} disabled={!editable} onChange={(e) => patchCustos('outros', e.target.value)} /></Field>
                </Grid2>

                {/* Anexos */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${BD.grayBorder}` }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Notas Fiscais e Comprovantes ({detail.anexos.length}/20)</div>
                  {detail.anexos.map((a) => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
                      <span style={{ color: BD.black, cursor: 'pointer' }} onClick={() => setViewAnexo({ url: a.arquivoUrlView ?? '', tipo: a.tipo, nome: a.nomeDespesa })}>📎 {a.nomeDespesa}</span>
                      {editable && (deletingAnexoId === a.id
                        ? <span style={{ color: BD.grayText, fontSize: 12 }}>removendo…</span>
                        : <span style={{ color: BD.red, cursor: 'pointer' }} onClick={() => removeAnexo(a.id)}>remover</span>
                      )}
                    </div>
                  ))}
                  {editable && detail.anexos.length < 20 && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setAnexoFile(e.target.files?.[0] ?? null)} />
                      {anexoFile && (
                        <Button onClick={enviarAnexo} disabled={uploadingAnexo} style={{ padding: '6px 14px' }}>
                          {uploadingAnexo ? 'Enviando…' : 'Enviar anexo'}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </SectionCard>

              {/* 3. Atendimento */}
              <SectionCard title="3. Informações de Atendimento">
                <div style={{ fontSize: 12, fontWeight: 600, color: BD.grayText, marginBottom: 8, textTransform: 'uppercase' }}>Quebra por setor</div>
                {form.leitorSetores.length === 0 ? (
                  <div style={{ fontSize: 13, color: BD.grayText }}>
                    Sem setores com ingressos emitidos para este evento.
                  </div>
                ) : (
                  <table style={{ width: '100%', fontSize: 13 }}>
                    <thead><tr style={{ color: BD.grayText, fontSize: 12 }}>
                      <th style={{ textAlign: 'left' }}>Setor</th><th style={{ textAlign: 'right' }}>Emitidos</th><th style={{ textAlign: 'right' }}>Sugeridos</th><th style={{ textAlign: 'center' }}>Utilizados</th><th style={{ textAlign: 'left' }}>Origem</th>
                    </tr></thead>
                    <tbody>
                      {form.leitorSetores.map((s, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${BD.grayBorder}` }}>
                          <td style={{ padding: '6px 0' }}><Pill $bg="#F3F4F6" $color="#4B5563">{s.setor}</Pill></td>
                          <td style={{ textAlign: 'right' }}>{s.emitidos.toLocaleString('pt-BR')}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{calcLeitoresSugeridos(s.emitidos, cfg)}</td>
                          <td style={{ textAlign: 'center' }}><Input type="number" style={{ width: 80, textAlign: 'center' }} value={s.leitoresUtilizados || ''} disabled={!editable} onChange={(e) => updSetor(i, { leitoresUtilizados: num(e.target.value) })} /></td>
                          <td>
                            {/* Sem fallback visual: mostrar "BD" com estado null salvava origem
                                vazia ("Não informada" no Analytics). Placeholder explícito +
                                borda vermelha quando há leitores utilizados sem origem. */}
                            <Select
                              value={s.origem ?? ''}
                              disabled={!editable}
                              style={
                                (s.leitoresUtilizados ?? 0) > 0 && !s.origem
                                  ? { borderColor: BD.red }
                                  : undefined
                              }
                              onChange={(e) => updSetor(i, { origem: (e.target.value || null) as any })}
                            >
                              <option value="">Selecione…</option>
                              {ORIGENS.map((o) => <option key={o}>{o}</option>)}
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: `2px solid ${BD.grayBorder}`, fontWeight: 700 }}>
                        <td style={{ padding: '8px 0' }}>TOTAL</td>
                        <td style={{ textAlign: 'right' }}>
                          {form.leitorSetores.reduce((acc, x) => acc + (x.emitidos || 0), 0).toLocaleString('pt-BR')}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {form.leitorSetores.reduce((acc, x) => acc + calcLeitoresSugeridos(x.emitidos, cfg), 0)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {form.leitorSetores.reduce((acc, x) => acc + (x.leitoresUtilizados || 0), 0)}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                )}

                <div style={{ fontWeight: 600, fontSize: 14, marginTop: 16, marginBottom: 8 }}>Máquinas levadas à bilheteria</div>
                {form.maquinas.map((m, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 6 }}>
                    <Field style={{ flex: 1 }}><label>Tipo</label><Input value={m.tipo} disabled={!editable} onChange={(e) => updMaquina(i, { tipo: e.target.value })} /></Field>
                    <Field style={{ flex: 1 }}><label>Serial / Patrimônio</label><Input value={m.serial ?? ''} disabled={!editable} onChange={(e) => updMaquina(i, { serial: e.target.value })} /></Field>
                    {editable && <Button $variant="secondary" onClick={() => rmMaquina(i)} style={{ padding: '6px 10px' }}>✕</Button>}
                  </div>
                ))}
                {editable && <Button $variant="secondary" onClick={addMaquina} style={{ fontSize: 12 }}>+ Adicionar máquina</Button>}

                <Field style={{ marginTop: 16 }}>
                  <label>Observações</label>
                  <Input as="textarea" rows={3} value={form.obsAtendimento} disabled={!editable} onChange={(e: any) => patch({ obsAtendimento: e.target.value })} />
                </Field>
              </SectionCard>
            </>
          )}

          {/* 4. Equipamentos para retorno (sempre) */}
          <SectionCard title="4. Equipamentos para Retorno" right={<span style={{ fontSize: 11, color: BD.grayText }}>sempre editável</span>}>
            <table style={{ width: '100%', fontSize: 13 }}>
              <thead><tr style={{ color: BD.grayText, fontSize: 12, borderBottom: `1px solid ${BD.grayBorder}` }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Equipamento</th><th style={{ textAlign: 'right' }}>Vlr. Unit.</th><th style={{ textAlign: 'center' }}>Qtd</th><th style={{ textAlign: 'right' }}>Total</th><th />
              </tr></thead>
              <tbody>
                {form.equipamentos.map((e, i) => (
                  <tr key={i}>
                    <td style={{ padding: 6 }}><Select value={e.tipo} disabled={!editable} onChange={(ev) => updEquip(i, { tipo: ev.target.value as OsEquipamentoTipo })}>{EQUIP_TIPOS.map((t) => <option key={t}>{t}</option>)}</Select></td>
                    <td><MoneyInput value={num(e.valorUnit)} disabled={!editable} onChange={(v) => updEquip(i, { valorUnit: v })} style={{ width: 130 }} /></td>
                    <td><Input type="number" style={{ width: 70, textAlign: 'center' }} value={e.qtd || ''} disabled={!editable} onChange={(ev) => updEquip(i, { qtd: num(ev.target.value) })} /></td>
                    <td style={{ textAlign: 'right' }}>{fmtMoney(num(e.qtd) * num(e.valorUnit))}</td>
                    <td style={{ textAlign: 'right' }}>{editable && <span style={{ color: BD.red, cursor: 'pointer' }} onClick={() => rmEquip(i)}>✕</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {editable && <Button $variant="secondary" onClick={addEquip} style={{ fontSize: 12, marginTop: 8 }}>+ Adicionar equipamento</Button>}
          </SectionCard>

          {/* 5. Logística de Equipamentos (custos livres) */}
          <SectionCard title="5. Logística de Equipamentos" right={<span style={{ fontSize: 11, color: BD.grayText }}>custos avulsos</span>}>
            <table style={{ width: '100%', fontSize: 13 }}>
              <thead><tr style={{ color: BD.grayText, fontSize: 12, borderBottom: `1px solid ${BD.grayBorder}` }}>
                <th style={{ textAlign: 'left', padding: 6 }}>Data</th>
                <th style={{ textAlign: 'left' }}>Descrição</th>
                <th style={{ textAlign: 'left' }}>Lançado por</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th />
              </tr></thead>
              <tbody>
                {(detail.custoLogistica ?? []).length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 10, color: '#9CA3AF', textAlign: 'center' }}>Nenhum custo de logística.</td></tr>
                )}
                {(detail.custoLogistica ?? []).map((l) => (
                  <tr key={l.id}>
                    <td style={{ padding: 6 }}>{fmtDate(l.data)}</td>
                    <td>{l.descricao}</td>
                    <td style={{ color: BD.grayText }}>{l.usuarioNome ?? '—'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(l.valor)}</td>
                    <td style={{ textAlign: 'right' }}>{editable && <span style={{ color: BD.red, cursor: 'pointer' }} onClick={() => removeLogistica(l.id)}>✕</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {editable && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
                <Field><label>Data</label><Input type="date" value={logForm.data} onChange={(e) => setLogForm((f) => ({ ...f, data: e.target.value }))} /></Field>
                <Field style={{ flex: 1, minWidth: 160 }}><label>Descrição</label><Input value={logForm.descricao} onChange={(e) => setLogForm((f) => ({ ...f, descricao: e.target.value }))} /></Field>
                <Field><label>Valor</label><MoneyInput value={logForm.valor} onChange={(v) => setLogForm((f) => ({ ...f, valor: v }))} style={{ width: 140 }} /></Field>
                <Button $variant="secondary" onClick={addLogistica} disabled={savingLog} style={{ fontSize: 12 }}>+ Adicionar</Button>
              </div>
            )}
          </SectionCard>
        </div>

        {/* ---- Resumo lateral ---- */}
        <div>
          <Card style={{ position: 'sticky', top: 20 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 12 }}>Resumo Financeiro</h3>
            {([['Técnicos (cachê)', resumo.cache], ['Staff', resumo.staff], ['Alimentação', resumo.alim], ['Deslocamento', resumo.desloc], ['Hospedagem', resumo.hosp]] as const).map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
                <span style={{ color: BD.grayText }}>{l}</span><span style={{ fontWeight: 600 }}>{fmtMoney(v)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `2px solid ${BD.red}`, marginTop: 6 }}>
              <span style={{ fontWeight: 700 }}>Custo Operacional</span><span style={{ fontWeight: 700, color: BD.red }}>{fmtMoney(resumo.op)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
              <span style={{ color: BD.grayText }}>Equipamentos</span><span style={{ fontWeight: 600 }}>{fmtMoney(resumo.equip)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
              <span style={{ color: BD.grayText }}>Logística</span><span style={{ fontWeight: 600 }}>{fmtMoney(resumo.logistica)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: `2px solid ${BD.red}` }}>
              <span style={{ fontWeight: 800, fontSize: 16 }}>Custo Total</span><span style={{ fontWeight: 800, fontSize: 16, color: BD.red }}>{fmtMoney(resumo.total)}</span>
            </div>

            {/* Ações */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BD.grayBorder}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {editable && (
                <>
                  <Button onClick={doSave} disabled={saving}>Salvar rascunho</Button>
                  <Button $variant="secondary" onClick={doSubmit} disabled={saving}>Enviar para aprovação</Button>
                </>
              )}
              {status === 'AGUARDANDO_APROVACAO' && canApprove && (
                <>
                  <Button onClick={() => doTransition(() => approveOs(osId), 'OS aprovada')} disabled={saving}>Aprovar</Button>
                  <Button $variant="secondary" onClick={doReject} disabled={saving}>Rejeitar</Button>
                </>
              )}
              {status === 'APROVADA' && canApprove && detail.os.importStatus !== 'IMPORTADA' && (
                <Button $variant="secondary" onClick={() => doTransition(() => reopenOs(osId), 'OS reaberta')} disabled={saving}>Reabrir</Button>
              )}
              {EDITABLE_STATUS.includes(status) && detail.os.importStatus !== 'IMPORTADA' && canPartialImport && (
                <Button $variant="secondary" onClick={doImportarParcial} disabled={saving}>Importar parcial (P2)</Button>
              )}
              {status === 'APROVADA' && detail.os.importStatus !== 'IMPORTADA' && canImport && (
                <Button onClick={doImport} disabled={saving}>Importar para o P2</Button>
              )}
              {detail.os.importStatus === 'PARCIAL' && (
                <div style={{ background: '#FEF3C7', color: '#92400E', padding: 8, borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
                  ◐ Parcialmente importada
                </div>
              )}
              {detail.os.importStatus === 'IMPORTADA' && (
                <div style={{ background: '#DBEAFE', color: '#1E3A8A', padding: 8, borderRadius: 6, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
                  ✓ Importada para o P2
                </div>
              )}
              {detail.os.importStatus !== 'PENDENTE' && canRevert && (
                <Button $variant="secondary" onClick={doReverter} disabled={saving}>Reverter importação</Button>
              )}
            </div>

            {detail.os.motivoRejeicao && status === 'REJEITADA' && (
              <div style={{ marginTop: 12, background: '#FEE2E2', color: '#991B1B', padding: 8, borderRadius: 6, fontSize: 12 }}>
                <strong>Rejeitada:</strong> {detail.os.motivoRejeicao}
              </div>
            )}

            {/* Histórico */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${BD.grayBorder}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: BD.grayText, marginBottom: 8 }}>HISTÓRICO</div>
              <div style={{ fontSize: 12, maxHeight: 200, overflowY: 'auto' }}>
                {detail.logs.map((l) => (
                  <div key={l.id} style={{ marginBottom: 6, color: BD.grayText }}>
                    <strong>{l.acao}</strong> · {l.usuarioNome ?? 'sistema'} · {fmtDateTime(l.criadoEm)}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </DetailLayout>

      {viewAnexo && (
        <div
          onClick={() => setViewAnexo(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 16, maxWidth: '92vw', maxHeight: '92vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 16 }}>
              <strong>{viewAnexo.nome}</strong>
              <span style={{ cursor: 'pointer', fontSize: 18 }} onClick={() => setViewAnexo(null)}>✕</span>
            </div>
            {viewAnexo.tipo === 'application/pdf' ? (
              <iframe title={viewAnexo.nome} src={viewAnexo.url} style={{ width: '85vw', height: '80vh', border: 'none' }} />
            ) : (
              <img src={viewAnexo.url} alt={viewAnexo.nome} style={{ maxWidth: '88vw', maxHeight: '80vh', display: 'block' }} />
            )}
            <div style={{ marginTop: 8 }}>
              <a href={viewAnexo.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: BD.red }}>Abrir em nova aba</a>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OsDetail
